import { tracking } from '@iwoca/frontend-tracking-library';
import { Pill } from '@iwoca/orion';

import {
  checkHasThirtyDayAndThreeMonthLimit,
  formatDuration,
} from './components/duration.helpers';
import { RefreshExpiredLimits } from './components/RefreshExpiredLimits';
import { SpendingLimitBar } from './components/SpendingLimitBar';
import {
  useSuspenseGetAccounts,
  useSuspenseGetApplication,
  useSuspenseGetBuyerSpendingLimit,
} from '../../../../api/lending/lapiHooks.suspense';
import {
  daysSinceStatusDate,
  getIwocaPayApplication,
  getSpendingLimitStatus,
} from '../../Dashboard.helpers';
import { useSuspenseGetSpendingLimits } from '../../hooks/useGetSpendingLimits';
import { formatCurrency } from '../../utils/currency';

export const SpendingLimitCardSkeleton = () => (
  <div className="grid grid-cols-[1fr_auto] gap-y-l [grid-template-areas:'left_right'_'bar_bar'] m:grid-cols-1 m:gap-y-s m:[grid-template-areas:'left'_'bar'_'right']">
    <div className="[grid-area:left]">
      <div className="h-[32px] w-[424px] animate-pulse rounded-xs bg-structure-95 m:w-[200px]" />
      <div className="mt-[8px] h-[16px] w-[288px] animate-pulse rounded-xs bg-structure-95 m:w-[180px]" />
    </div>
    <div className="justify-items-end [grid-area:right] m:justify-items-start">
      <div className="h-[16px] w-[160px] animate-pulse rounded-xs bg-structure-95" />
      <div className="mt-[8px] h-[12px] w-[96px] animate-pulse rounded-xs bg-structure-95" />
    </div>
    <div className="[grid-area:bar]">
      <div className="h-[8px] w-full animate-pulse rounded-2xl bg-structure-95" />
    </div>
  </div>
);

export const SpendingLimitCard = ({
  setIsSpendingLimitDrawerOpen,
  refreshSpendingLimit,
  loadingRefreshSpendingLimit,
}: {
  setIsSpendingLimitDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  refreshSpendingLimit: () => Promise<void>;
  loadingRefreshSpendingLimit: boolean;
}) => {
  const { application: rawApplication } = useSuspenseGetApplication();
  const { spendingLimit } = useSuspenseGetBuyerSpendingLimit();
  const { spendingLimits } = useSuspenseGetSpendingLimits();
  const { accounts } = useSuspenseGetAccounts();

  const application = rawApplication
    ? getIwocaPayApplication(rawApplication)
    : null;
  const activeSpendingLimit = spendingLimit?.data?.active_spending_limit;

  if (!activeSpendingLimit) return null;

  // Legacy compatibility to remove the old endpoint first
  // TODO: Check if this is still required?
  const status = getSpendingLimitStatus({
    spendingLimits: { isValid: activeSpendingLimit.is_valid },
    application,
  });

  const hasThirtyDayAndThreeMonthLimit = checkHasThirtyDayAndThreeMonthLimit(
    spendingLimits.limits,
  );

  const flexiLoan = accounts?.accounts?.find(
    (account) =>
      (account.product_type === 'credit_facility' ||
        account.product_type === 'flexi24') &&
      account.loan_status !== 'ended',
  );

  const isDeclined = status === 'declined';
  const isExpired = status === 'expired';

  const displayAmount = isDeclined ? 0 : activeSpendingLimit.available_amount;
  const displayMaxAmount = isDeclined ? 0 : activeSpendingLimit.max_amount;

  const daysSinceDecline = isDeclined
    ? daysSinceStatusDate(application!)
    : null;
  const showRefreshButton =
    isExpired ||
    (isDeclined && (daysSinceDecline !== null ? daysSinceDecline >= 30 : true));

  return (
    <div className="grid grid-cols-[1fr_auto] gap-y-l [grid-template-areas:'left_right'_'bar_bar'] m:grid-cols-1 m:gap-y-s m:[grid-template-areas:'left'_'bar'_'right']">
      <div className="[grid-area:left]">
        <div className="flex flex-wrap items-center gap-s text-2xl font-reg m:text-xl">
          You have{' '}
          <span className="font-bold">{formatCurrency(displayAmount)}</span>{' '}
          left to spend
          {isExpired && <Pill size="small">Estimated</Pill>}
        </div>
        <div className="mt-xs text-s font-reg">
          {status === 'valid' && '*Interest rate is dependent on supplier'}
          {isExpired &&
            'This is an estimated amount based on your last transaction with us \u2014 refresh it by clicking \u201cSecure my limit\u201d or make a purchase with Pay Later.'}
          {isDeclined &&
            'We can\u2019t approve you for more funds right now \u2014 check back in 30 days and hit \u2018Secure my limit\u2019.'}
        </div>
        {status === 'valid' &&
          activeSpendingLimit.max_amount === 150 &&
          flexiLoan && (
            <div className="mt-xs text-s font-reg text-structure-40">
              *Your available spending is limited due to active loans.
            </div>
          )}
      </div>

      <div className="justify-self-end [grid-area:right] m:justify-self-start">
        <div className="flex flex-wrap justify-end gap-s text-s font-med m:justify-start m:font-reg">
          <div>
            {isExpired ? 'Estimated spending limit: ' : 'Spending limit: '}
            {formatCurrency(displayMaxAmount)}
          </div>
          {showRefreshButton && (
            <RefreshExpiredLimits
              loading={loadingRefreshSpendingLimit}
              setIsSpendingLimitDrawerOpen={setIsSpendingLimitDrawerOpen}
              refreshSpendingLimit={refreshSpendingLimit}
            />
          )}
        </div>
        <div className="text-right text-xs font-reg m:text-left">
          {formatDuration({
            duration: activeSpendingLimit.duration_in_months,
            groupSameLimits: hasThirtyDayAndThreeMonthLimit,
          })}{' '}
          purchases{' '}
          <button
            onClick={() => {
              tracking.ampli.spendingLimitViewedOtherTerms();
              setIsSpendingLimitDrawerOpen(true);
            }}
            className="ml-xs cursor-pointer border-none bg-transparent p-0 text-xs text-[color:var(--color-primary-40,#4b4bff)] underline"
          >
            Other terms
          </button>
        </div>
      </div>

      <div className="[grid-area:bar]">
        <SpendingLimitBar status={status} />
      </div>
    </div>
  );
};
